import { defineStore } from "pinia"

import isEqual from "lodash/isEqual"

import * as groupApi from "@/api/groupApi.js"

import { generateUUID } from "@/services/texts"
import * as sentryReporter from "@/services/sentryReporter.js"

import { groupTypes } from "@/constants/groups.js"

import { useUserSessionStore } from "@/store/userSession.js"
import { useConfigurationStore } from "@/store/configuration.js"

//We don't support immersive groups currently
const groupIsNotImmersive = (group) => group.layout !== "immersive"

const setGroupDetails = (groups, unreadPosts) => {
  groups.forEach((g) => {
    if (unreadPosts) {
      g.unreadPosts = unreadPosts[g.key] || 0
    }
    g.imageUrl = g.image?.versions?.group_small?.base_url
    g.largeImageUrl = g.header_image?.versions?.group_header_large?.base_url || g.image?.versions?.group_large?.base_url
  })
}

//Merge groups (instead of reassigning arrays) to avoid losing references to objects
const mergeGroups = (groups, groupsToMerge) => {
  if (isEqual(groups, groupsToMerge)) {
    return
  }

  const newGroups = groupsToMerge.filter((gm) => !groups.map((g) => g.key).includes(gm.key))
  const deletedGroups = groups.filter((gm) => !groupsToMerge.map((g) => g.key).includes(gm.key))

  if (deletedGroups.length) {
    for (let i = groups.length - 1; i >= 0; i--) {
      if (deletedGroups.includes(groups[i])) {
        groups.splice(i, 1)
      }
    }
  }

  //Update existing groups
  groups.forEach((g) => {
    const relatedGroup = groupsToMerge.find((gm) => gm.key === g.key)
    if (relatedGroup) {
      Object.keys(relatedGroup).forEach((key) => (g[key] = relatedGroup[key]))
    }
  })

  //Add new groups
  newGroups.forEach((g) => groups.push(g))

  //Adjust order to groups from api
  groups.sort((a, b) => groupsToMerge.findIndex((g) => g.key === a.key) - groupsToMerge.findIndex((g) => g.key === b.key))
}

const activityFeedGroup = {
  key: generateUUID(),
  isActivityFeed: true
}

export const useGroupsStore = defineStore("groups", {
  state: () => ({
    unfilteredAllGroupsState: [],
    allGroupsState: [],
    allGroupsLoadingState: false,
    userGroupsState: [],
    membershipRequestsState: []
  }),
  getters: {
    groupsForName: (state) => (value) => state.allGroupsState.filter((g) => g.name.toLowerCase().includes(value.toLowerCase())),
    groupForKey: (state) => (value) => state.allGroupsState.find((g) => g.key === value),
    isGroupImmersive: (state) => (value) => Boolean(state.unfilteredAllGroupsState.find((g) => g.key === value)?.layout === "immersive"),
    isUserMember: (state) => (key) => state.userGroupsState.some((g) => g.key === key),
    userRoleForGroup: (state) => (key) => state.userGroupsState.find((g) => g.key === key)?.user_role,
    isUserAdmin: (state) => (key) => state.userGroupsState.find((g) => g.key === key)?.user_role === "admin",
    unfilteredAllGroups: (state) => state.unfilteredAllGroupsState,
    allGroups: (state) => state.allGroupsState,
    allGroupsLoading: (state) => state.allGroupsLoadingState,
    userGroups: (state) => {
      const userGroupKeys = state.userGroupsState.map((ug) => ug.key)

      //Use all groups as source so we don't mix group objects from 2 sources
      const userGroupsFromAllGroups = state.allGroupsState.filter((g) => userGroupKeys.includes(g.key))
      userGroupsFromAllGroups.sort((a, b) => userGroupKeys.indexOf(a.key) - userGroupKeys.indexOf(b.key))
      return userGroupsFromAllGroups
    },
    nonUserGroups: (state) => state.allGroupsState.filter((g) => !state.userGroupsState.some((ug) => ug.key === g.key)),
    postableGroups: (state) => {
      const userPostableGroups = state.userGroupsState.filter(
        (g) => (g.posting_permission === "everyone" && g.user_role !== null) || (g.posting_permission === "admins_only" && g.user_role === "admin")
      )
      return state.allGroupsState.filter((g) => userPostableGroups.map((ug) => ug.key).includes(g.key))
    },
    userGroupsWithoutCourseGroups() {
      return this.userGroups.filter((group) => group.membership_type !== groupTypes.COURSE)
    },
    activityFeedGroup: () => {
      const configurationStore = useConfigurationStore()
      const userSessionStore = useUserSessionStore()

      activityFeedGroup.name = configurationStore.configuration.appTitle
      activityFeedGroup.description = configurationStore.configuration.registrationPageIntroduction

      activityFeedGroup.imageUrl = userSessionStore.logoUrl

      return activityFeedGroup
    },
    membershipRequests: (state) => state.membershipRequestsState,
    isMembershipRequested: (state) => (key) => state.membershipRequestsState.some((r) => r.group.key === key)
  },
  actions: {
    async refreshAllGroups() {
      this.allGroupsLoadingState = true
      const userSessionStore = useUserSessionStore()
      try {
        const [all, user] = await Promise.all([groupApi.groups(), groupApi.userGroups(), this.refreshMembershipRequests()])

        //Temporary trying to find out what is happening with this error:
        //Does API really returns no groups key?
        //https://disciple.sentry.io/issues/4928437937/events/latest/?project=6718259&query=is%3Aunresolved&referrer=latest-event&statsPeriod=90d&stream_index=14&utc=true
        if (!all.groups) {
          sentryReporter.reportError(new Error("No groups loaded"))
          if (typeof all === "string") {
            sentryReporter.reportError(new Error(all))
          }
        }
        if (!user.groups) {
          sentryReporter.reportError(new Error("No user groups loaded"))
        }

        setGroupDetails(all.groups)
        setGroupDetails(user.groups, userSessionStore.unreadPosts)

        mergeGroups(this.unfilteredAllGroupsState, all.groups)
        mergeGroups(this.allGroupsState, all.groups.filter(groupIsNotImmersive))
        mergeGroups(this.userGroupsState, user.groups.filter(groupIsNotImmersive))

        //UserGroups can contain secret or course groups which are not present in all groups endpoint's response
        const specialGroups = this.userGroupsState.filter((g) => [groupTypes.SECRET, groupTypes.COURSE].includes(g.membership_type))
        specialGroups.forEach((sg) => {
          if (!this.allGroupsState.some((g) => g.key === sg.key)) {
            this.allGroupsState.push(sg)
          }
        })

        //Set keys that are available only in userGroups
        this.allGroupsState.forEach((g) => {
          const userGroup = this.userGroupsState.find((ug) => ug.key === g.key)
          if (userGroup) {
            Object.keys(userGroup).forEach((key) => {
              if (g[key] === undefined) {
                g[key] = userGroup[key]
              }
            })
          }
        })
      } catch (err) {
        sentryReporter.reportError(err)
      } finally {
        this.allGroupsLoadingState = false
      }
    },
    async refreshMembershipRequests() {
      try {
        const { group_membership_requests: membershipRequests } = await groupApi.userMembershipRequests()
        this.membershipRequestsState = membershipRequests
      } catch (err) {
        sentryReporter.reportError(err)
      }
    },
    updateGroupMembers({ groupKey, count }) {
      const group = this.allGroupsState.find((g) => g.key === groupKey)
      if (group?.members_viewable) {
        group.members_count += count
      }
    },
    clearGroups() {
      this.allGroupsState = []
      this.allGroupsLoadingState = false
      this.userGroupsState = []
      this.membershipRequestsState = []
      this.unfilteredAllGroupsState = []
    },
    async joinGroup(group) {
      await groupApi.join(group.key)
      await this.refreshAllGroups()
    },
    async leaveGroup(group) {
      await groupApi.leave(group.key)
      await this.refreshAllGroups()
    },
    async cancelGroupRequest(group) {
      await groupApi.cancelRequest(group.key)
      await this.refreshMembershipRequests()
    }
  }
})
